import { TestableComponentProps } from '@dreryk/react-components';

import { Reimbursement, ReimbursementVariant } from '~libs.desktop.shared.medicine';

import { MagistralDrugHeading } from '../../../magistralDrug/MagistralDrugHeading';
import { MagistralDrugSubheading } from '../../../magistralDrug/MagistralDrugSubheading';
import { ActiveIngredientsQuantitiesTextValue } from '../../prescription/activeIngredients/ActiveIngredientsTextValue';
import { EntitlementTypesTextView } from '../../prescription/additionalEntitlements/EntitlementTypesTextView';
import { AdditionalRequirementsTextView } from '../../prescription/additionalRequirements/AdditionalRequirementsTextView';
import { DosageTextView } from '../../prescription/dosage/DosageTextView';
import { IssuedFromTextView } from '../../prescription/issuedDate/IssuedFromTextView';
import { IssuedToTextView } from '../../prescription/issuedDate/IssuedToTextView';
import { PrescriptionCard } from '../../prescription/PrescriptionCard';
import { PrescriptionCategoryTextView } from '../../prescription/prescriptionCategory/PrescriptionCategoryTextView';
import { QuantityTextView } from '../../prescription/quantity/QuantityTextView';
import { PrescriptionStatusBox } from '../../prescription/status/PrescriptionStatusBoxPortal';
import { SentPrescriptionAnnulmentLink } from '../common/SentPrescriptionAnnulmentLink';
import { SentPrescriptionStatus } from '../SentPrescriptionStatus';

import { SentMagistralDrugPrescriptionGrid } from './SentMagistralDrugPrescriptionGrid';
import { SentMagistralDrugPrescription } from './SentMagistralDrugPrescriptionItem.types';

type SentMagistralDrugPrescriptionItemProps = TestableComponentProps &
   SentMagistralDrugPrescription & {
      readonly visitId?: number;
   };

export const SentMagistralDrugPrescriptionItem = (props: SentMagistralDrugPrescriptionItemProps): JSX.Element => {
   const magistralDrug = props.medicinePrescription.magistralDrug!;

   return (
      <PrescriptionCard
         testId={props.testId}
         heading={
            <MagistralDrugHeading
               name={magistralDrug.name}
               magistralDrugMedicineRawMaterials={magistralDrug.magistralDrugMedicineRawMaterials}
            />
         }
         subheading={
            <MagistralDrugSubheading
               name={magistralDrug.name}
               magistralDrugMedicineRawMaterials={magistralDrug.magistralDrugMedicineRawMaterials}
               additionalDetails={magistralDrug.additionalDetails}
               form={magistralDrug.form}
            />
         }
         annulled={props.annulled ? <SentPrescriptionStatus /> : null}
         options={
            props.annulled ? null : (
               <SentPrescriptionAnnulmentLink
                  disabled={!props.annullable}
                  prescriptionId={props.id}
                  visitId={props.visitId!}
               />
            )
         }
      >
         <PrescriptionStatusBox prescriptionId={props.id} />

         {props.reimbursement ? (
            <Reimbursement
               code={props.reimbursement.code}
               priceDescription={props.reimbursement.priceDescription}
               variant={ReimbursementVariant.STANDARD}
               active
               disabled
            />
         ) : null}

         <SentMagistralDrugPrescriptionGrid
            dosage={<DosageTextView dosage={props.dosage} />}
            issuedFrom={<IssuedFromTextView issuedFrom={props.issuedFrom} />}
            quantity={<QuantityTextView quantity={props.quantity} />}
            issuedTo={<IssuedToTextView issuedTo={props.issuedTo} />}
            activeIngredients={
               <ActiveIngredientsQuantitiesTextValue
                  medicinePrescription={props.medicinePrescription}
                  quantity={props.quantity}
               />
            }
            additionalEntitlementTypes={
               <EntitlementTypesTextView entitlementTypes={props.additionalEntitlementTypes} />
            }
            additionalRequirementTypes={
               <AdditionalRequirementsTextView additionalRequirementTypes={props.additionalRequirementTypes} />
            }
            prescriptionCategory={
               props.prescriptionCategory ? (
                  <PrescriptionCategoryTextView prescriptionCategory={props.prescriptionCategory} />
               ) : undefined
            }
         />
      </PrescriptionCard>
   );
};
